import React, { useCallback, useState, useEffect } from "react";
import {loadStripe} from '@stripe/stripe-js';
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout
} from '@stripe/react-stripe-js';
import {
  Navigate,
  useNavigate
} from "react-router-dom";
import axios from 'axios';
import config from '../config';

// Make sure to call `loadStripe` outside of a component's render to avoid
// recreating the `Stripe` object on every render.
// Load the Stripe publishable key from environment variables
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const SubscriptionBenefits = () => {
  const benefits = [
    "Full access to hundreds of user chats and responses - Find out what users are asking about your business",
    "Citation sources - Find out where AI chatbots get their information",
    "Access to sentiment scores and sources - Get ahead of negative comments",
    "Tips and insights proven to improve your rankings",
    "Reports sent to your email each month"
  ];

  return (
    <div>
      <h2 style={{
        fontSize: '24px',
        fontWeight: '600',
        marginBottom: '24px',
        color: '#1a1a1a'
      }}>
        What's Included
      </h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {benefits.map((benefit, index) => (
          <div key={index} style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px'
          }}>
            <span style={{
              color: '#4CAF50',
              fontSize: '20px',
              lineHeight: '24px',
              flexShrink: 0
            }}>✓</span>
            <span style={{
              fontSize: '16px',
              lineHeight: '24px',
              color: '#4a4a4a'
            }}>{benefit}</span>
          </div>
        ))}
      </div>
      <h4 style={{
        fontSize: '18px',
        fontWeight: '600',
        marginBottom: '24px',
        color: '#1a1a1a'
      }}>
        No long term commitment or recurring charges
      </h4>
      
      <div style={{ marginTop: '40px' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '16px',
          marginTop: '100px',
          marginBottom: '20px'
        }}>
          <img src="/images/CompanyLogos/1.avif" alt="Company logo" style={{ width: '100%', height: 'auto', objectFit: 'contain', maxHeight: '40px' }} />
          <img src="/images/CompanyLogos/2.avif" alt="Company logo" style={{ width: '100%', height: 'auto', objectFit: 'contain', maxHeight: '40px' }} />
          <img src="/images/CompanyLogos/3.png" alt="Company logo" style={{ width: '100%', height: 'auto', objectFit: 'contain', maxHeight: '40px' }} />
          <img src="/images/CompanyLogos/4.png" alt="Company logo" style={{ width: '100%', height: 'auto', objectFit: 'contain', maxHeight: '40px' }} />
          <img src="/images/CompanyLogos/5.png" alt="Company logo" style={{ width: '100%', height: 'auto', objectFit: 'contain', maxHeight: '40px' }} />
          <img src="/images/CompanyLogos/6.png" alt="Company logo" style={{ width: '100%', height: 'auto', objectFit: 'contain', maxHeight: '40px' }} />
          <img src="/images/CompanyLogos/7.avif" alt="Company logo" style={{ width: '100%', height: 'auto', objectFit: 'contain', maxHeight: '40px' }} />
          <img src="/images/CompanyLogos/8.png" alt="Company logo" style={{ width: '100%', height: 'auto', objectFit: 'contain', maxHeight: '40px' }} />
        </div>
        <p style={{
          fontSize: '18px',
          color: '#6c757d',
          textAlign: 'center',
          margin: 0
        }}>
          4000+ businesses are claiming their advantage in the AI era
        </p>
      </div>
      
    </div>
  );
};

export const CheckoutForm = () => {
  const fetchClientSecret = useCallback(() => {
    // Create a Checkout Session
    return fetch(`${config.API_URL}/api/create-checkout-session`, {
      method: "POST",
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then((res) => res.json())
      .then((data) => data.clientSecret);
  }, []);

  const options = {fetchClientSecret};

  return (
    <div className="checkout-container" style={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      overflowX: 'auto',
      overflowY: 'auto'
    }}>
      <div style={{
        minWidth: '830px',
        padding: '40px 20px'
      }}>
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '40px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          display: 'flex',
          gap: '60px',
          alignItems: 'flex-start',
          flexDirection: 'row',
          minWidth: '750px',
          maxWidth: '1100px',
          margin: '0 auto',
          boxSizing: 'border-box'
        }}>
          <div style={{
            flex: '1 1 40%',
            minWidth: '200px',
            borderRight: '1px solid #e0e0e0',
            paddingRight: '40px'
          }}>
            <SubscriptionBenefits />
          </div>
          <div id="checkout" style={{
            flex: '1 1 60%',
            minWidth: '400px'
          }}>
            <EmbeddedCheckoutProvider
              stripe={stripePromise}
              options={options}
            >
              <EmbeddedCheckout />
            </EmbeddedCheckoutProvider>
          </div>
        </div>
      </div>
    </div>
  )
}

export const ReturnForm = () => {
  const [status, setStatus] = useState(null);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const sessionId = urlParams.get('session_id');

    fetch(`${config.API_URL}/api/session-status?session_id=${sessionId}`)
      .then((res) => res.json())
      .then((data) => {
        setStatus(data.status);
        setEmail(data.customer_email); // Pre-populate email field

        // Set purchase status in sessionStorage when payment is complete
        if (data.status === 'complete') {
          sessionStorage.setItem('hasPurchased', 'true');
        }
      });
  }, []);

  if (status === 'open') {
    return (
      <Navigate to="/checkout" />
    )
  }

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Get business data from sessionStorage
      const businessDataStr = sessionStorage.getItem('businessData');

      if (!businessDataStr) {
        alert('Session expired. Please start over.');
        navigate('/');
        return;
      }

      const businessData = JSON.parse(businessDataStr);

      // Submit email and log PostHog event (non-blocking for better UX)
      axios.post(`${config.API_URL}/api/submit-email`, {
        email,
        businessData
      }).catch(err => {
        // Log but don't block the user flow
        console.error('Failed to log email submission:', err);
      });

      // Store email and flag for paid analysis in sessionStorage
      sessionStorage.setItem('userEmail', email);
      sessionStorage.setItem('runPaidAnalysis', 'true');

      // Navigate to home to trigger analysis with loading animation
      navigate('/');

    } catch (error) {
      console.error('Error submitting email:', error);
      alert('An error occurred. Please try again or contact support.');
      setIsSubmitting(false);
    }
  };

  if (status === 'complete') {
    return (
      <div className="typeform-container" style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        <div className="typeform-content" style={{
          maxWidth: '500px',
          width: '100%',
          background: 'white',
          borderRadius: '12px',
          padding: '40px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
        }}>
          <h2 style={{
            marginBottom: '20px',
            fontSize: '28px',
            fontWeight: '600',
            color: '#1a1a1a',
            textAlign: 'center'
          }}>Payment Successful!</h2>
          <p style={{
            marginBottom: '30px',
            fontSize: '16px',
            color: '#4a4a4a',
            textAlign: 'center',
            lineHeight: '1.6'
          }}>
            We'll email you a copy of your report. Please confirm your email address below.
          </p>
          <form onSubmit={handleEmailSubmit} style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '20px'
          }}>
            <div>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '14px',
                fontWeight: '500',
                color: '#333'
              }}>
                Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="your@email.com"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  fontSize: '16px',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  outline: 'none',
                  transition: 'border-color 0.2s',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => e.target.style.borderColor = '#667eea'}
                onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
              />
            </div>
            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                width: '100%',
                padding: '14px',
                fontSize: '16px',
                fontWeight: '600',
                color: 'white',
                background: isSubmitting
                  ? '#cccccc'
                  : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                border: 'none',
                borderRadius: '8px',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s'
              }}
            >
              {isSubmitting ? 'Generating Your Report...' : 'Get My Report'}
            </button>
          </form>
        </div>
      </div>
    )
  }

  return null;
}